<template>
  <b-card no-body>
    <b-card-header>
      <div>
        <b-card-title class="font-weight-bolder mb-1">
          Inventory Expiring Soon
        </b-card-title>
      </div>
      <div>
        <b-button
          :id="'btn-filter-expiring-inv'"
          variant="outline-primary"
          size="sm"
        >
          <feather-icon icon="FilterIcon" size="14" />
          Filters
        </b-button>
        <b-popover
          target="btn-filter-expiring-inv"
          triggers="click blur"
          placement="bottom"
          custom-class="popover-until-exp-filter-wide"
        >
          <template #title>Filter Options</template>
          <b-form @submit.prevent="applyFilters">
            <b-form-group label="Min Weeks Until Expired" label-size="sm">
              <b-form-input
                v-model="filterForm.minWeeksUntilExpired"
                type="number"
                step="0.1"
                size="sm"
                placeholder="e.g. 1"
              />
            </b-form-group>
            <b-form-group label="Max Weeks Until Expired" label-size="sm">
              <b-form-input
                v-model="filterForm.maxWeeksUntilExpired"
                type="number"
                step="0.1"
                size="sm"
                placeholder="e.g. 16"
              />
            </b-form-group>
            <b-form-group label="Minimum Quantity" label-size="sm">
              <b-form-input
                v-model="filterForm.minQuantity"
                type="number"
                step="1"
                size="sm"
                placeholder="e.g. 10"
              />
            </b-form-group>
            <div class="d-flex justify-content-between mt-1">
              <b-button variant="outline-secondary" size="sm" @click="resetFilters">
                Reset
              </b-button>
              <b-button variant="primary" size="sm" type="submit">
                Apply
              </b-button>
            </div>
          </b-form>
        </b-popover>
      </div>
    </b-card-header>

    <b-card-body class="p-0">
      <div v-if="items.length>0">
        <b-alert show variant="warning" class="m-0">
          <p>The inventory below is expected to expire long before it will be sold out.</p><p> It is recommended you review the items below, and adjust your sales policy as needed.</p>
        </b-alert>
      </div>
      <div class="text-center" v-if="isLoading">
        <b-spinner
          variant="primary"
          class="m-5"
          style="height: 4rem; width: 4rem"
          label="Loading..."
        ></b-spinner>
      </div>
      <div v-else class="inv-expires-tablewrap">
        <TableListing
          :hide-pagination-top-fields="true"
          :responsive="true"
          moreCustom5Class="p-0"
          :fixed="false"
          sticky-header
          :loading-page="isLoading"
          :items.sync="items"
          :fields.sync="tableColumns"
          :pagination.sync="pagination"
          :options.sync="tableOptions"
          :bordered="false"
          v-on:pagination-changed="onPaginationChanged"
        >
          <template v-slot:cell(expirationDate)="data">
            <span v-if="data.item.expirationDate">{{
              data.item.expirationDate | moment('MMM Do YYYY')
            }}</span>
            <span v-else>-</span>
          </template>
          <template v-slot:cell(weeksUntilSold)="data">
            <span v-if="data.item.weeksUntilSold > (data.item.weeksUntilExpired * 3)" class="text-danger font-weight-bold">{{ data.item.weeksUntilSold.toFixed(1) }}</span>
            <span v-else class="text-warning font-weight-bold">{{ data.item.weeksUntilSold.toFixed(1) }}</span>
            <span> weeks</span>
          </template>
          <template v-slot:cell(weeksUntilExpired)="data">
            <span
              :class="{
                'text-danger font-weight-bold': data.item.weeksUntilExpired < 8,
                'text-warning': data.item.weeksUntilExpired >= 8 && data.item.weeksUntilExpired < 16
              }"
            >{{ data.item.weeksUntilExpired.toFixed(1) }}</span>
            <span> weeks</span>
          </template>
          <template v-slot:cell(quantity)="data">
            {{ data.item.quantity }}
          </template>
          <template v-slot:cell(item)="data">
            <div>{{ data.item.item }}</div>
            <div class="text-muted text-small">{{ data.item.description }}</div>
          </template>
          <template v-slot:cell(warehouseId)="data">
            <span
              v-if="myWarehouses && myWarehouses.find(a => a.id == data.item.warehouseId)"
              >{{ myWarehouses.find(a => a.id == data.item.warehouseId).name }}</span
            >
            <span v-else>-</span>
          </template>
          <template v-slot:cell(brandId)="data">
            <span
              v-if="myBrands && myBrands.find(a => a.id == data.item.brandId)"
              >{{ myBrands.find(a => a.id == data.item.brandId).name }}</span
            >
            <span v-else>-</span>
          </template>
        </TableListing>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import { TableListing } from '@/views/components/custom';
import {
  BCard,
  BCardHeader,
  BCardBody,
  BButton,
  BSpinner,
  BCardTitle,
  BPopover,
  BForm,
  BFormGroup,
  BFormInput
} from 'bootstrap-vue';

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BButton,
    BSpinner,
    BCardTitle,
    BPopover,
    BForm,
    BFormGroup,
    BFormInput,
    TableListing
  },
  data() {
    return {
      tableOptions: {
        totalRows: 0,
        loadedAll: false
      },
      pagination: {
        perPage: 50,
        page: 1
      },
      filterForm: {
        minWeeksUntilExpired: 3,
        maxWeeksUntilExpired: 24,
        minQuantity: 30
      },
      appliedFilters: {
        minWeeksUntilExpired: 3,
        maxWeeksUntilExpired: 24,
        minQuantity: 30
      },
      items: [],
      tableColumns: [
        { key: 'warehouseId', label: 'Warehouse', sortable: false },
        { key: 'brandId', label: 'Client', sortable: false },
        { key: 'item', label: 'Item', sortable: false },
        { key: 'quantity', label: 'Qty', sortable: false },
        { key: 'lotNumber', label: 'Lot #', sortable: false },
        { key: 'expirationDate', label: 'Expires', sortable: false },
        { key: 'weeksUntilExpired', label: 'Until Expired', sortable: false },
        { key: 'weeksUntilSold', label: 'Until Sold Out', sortable: false }
      ],
      isLoading: true
    };
  },
  methods: {
    applyFilters() {
      this.appliedFilters = { ...this.filterForm };
      this.pagination.page = 1;
      this.loadData();
      this.$root.$emit('bv::hide::popover', 'btn-filter-expiring-inv');
    },
    resetFilters() {
      this.filterForm = {
        minWeeksUntilExpired: null,
        maxWeeksUntilExpired: null,
        minQuantity: null
      };
      this.appliedFilters = {};
      this.pagination.page = 1;
      this.loadData();
      this.$root.$emit('bv::hide::popover', 'btn-filter-expiring-inv');
    },
    onPaginationChanged(pag) {
      this.pagination = pag;
      this.loadData();
    },
    loadData() {
      this.isLoading = true;
      let payload = {
        page: this.pagination.page,
        perPage: this.pagination.perPage
      };
      if (this.appliedFilters.minWeeksUntilExpired) {
        payload.minWeeksUntilExpired = parseFloat(this.appliedFilters.minWeeksUntilExpired);
      }
      if (this.appliedFilters.maxWeeksUntilExpired) {
        payload.maxWeeksUntilExpired = parseFloat(this.appliedFilters.maxWeeksUntilExpired);
      }
      if (this.appliedFilters.minQuantity) {
        payload.minQuantity = parseFloat(this.appliedFilters.minQuantity);
      }
      this.$http
        .post('/api/common/analytics/inventory-expiring-soon', payload)
        .then(resp => {
          this.isLoading = false;
          this.items = resp.data.items;
          this.tableOptions.totalRows = resp.data.total;
          this.pagination.page = resp.data.page;
        })
        .catch(err => {
          this.isLoading = false;
          this.items = [];
        });
    }
  },
  mounted() {
    this.loadData();
  }
};
</script>

<style>
.popover-until-exp-filter-wide {
  min-width: 250px;
}
.inv-expires-tablewrap .table-responsive.b-table-sticky-header {
  max-height: 350px;
}
</style>
